import React, { useMemo } from 'react';
import { EventsListReservationStyled } from './events-list-reservation.styled';
import {
  getDateTimeBooking,
  getRoom,
  getStringDateName,
} from '../../../../../utils/utils';
import i18n from '../../../../../translations/strings';
import { SvgType } from '../../../../../components/base/svg/svg';
import {
  EventStatusPayment,
  ReservationBookingActionType,
} from '../../../../../store/admin/handle-reservation.store';
import ClickableTextProps from '../../../../../components/base/clickable-text/clickable-text';
import TableEventsReservation from '../table-events-reservation/table-events-reservation';
import { ListStyled } from '../../../../../components/shared/list/list.styled';
import { Colors } from '../../../../../colors';
import EditText from '../../../../../components/base/edit-text/edit-text';
import { EdiTextVariant } from '../../../../../components/base/edit-text/constants';
import LinearLoading from '../../../../../components/base/loading/linear-loading/linear-loading';
import { ModalVariant } from '../../../../../components/shared/modal-provider/components/modal/modal.constants';
import { useModal } from '../../../../../components/shared/modal-provider/hooks/modal-provider.hook';

export interface EventsListReservationsStyled {
  reservation: any;
  rooms: any;
  onCheckInOutClick: (data: any) => void;
  onEditReservationClick: () => void;
  onPaymentStatusChange: (item: any) => void;
  loading: boolean;
}

const EventsListReservation = (props: EventsListReservationsStyled) => {
  const { reservation, rooms } = props;
  const { openModal } = useModal();

  const openModalCheckInOut = (
    event: any,
    typeAction: ReservationBookingActionType,
  ) => {
    openModal({
      message: i18n.t('handleReservation.alertModalCHeckInOut'),
      variant: ModalVariant.ConfirmationRequest,
      onConfirmClick: () => sendCheckInOut(event, typeAction),
    });
  };

  const paymentStatusAlertModal = (item: any) => {
    openModal({
      message: i18n.t('handleReservation.paymentStatusAlert'),
      variant: ModalVariant.ConfirmationRequest,
      onConfirmClick: () => props.onPaymentStatusChange(item),
    });
  };

  const sendCheckInOut = (
    event: any,
    typeAction: ReservationBookingActionType,
  ) => {
    const data = {
      dataToSend: {
        action: typeAction,
        interval_id: event.id,
      },
      bookingId: reservation.id,
    };
    props.onCheckInOutClick(data);
  };

  const fields = useMemo(
    () => [
      {
        name: 'date',
        displayName: i18n.t('eventsList.date'),
        component: (item: any) => {
          const date = getStringDateName(item.start);
          return <ListStyled.CustomRow>{date}</ListStyled.CustomRow>;
        },
      },
      {
        name: 'time',
        displayName: i18n.t('eventsList.time'),
        component: (item: any) => {
          const { time: timeStart } = getDateTimeBooking(item.start);
          const { time: timeEnd } = getDateTimeBooking(item.end);
          return (
            <ListStyled.CustomRow>
              {i18n.t('reservations.timeStartEnd', {
                start: timeStart,
                end: timeEnd,
              })}
            </ListStyled.CustomRow>
          );
        },
      },
      {
        name: 'room',
        displayName: i18n.t('eventsList.room'),
        component: (item: any) => {
          const room = getRoom(rooms, item.product);
          return <ListStyled.CustomRow>{room?.name}</ListStyled.CustomRow>;
        },
      },
      {
        name: 'attendees',
        displayName: i18n.t('eventsList.inPresence'),
        component: (item: any) => {
          return (
            <ListStyled.CustomRow>
              {item.attendees ? item.attendees : 0}{' '}
              {i18n.t('eventsList.attendees')}
            </ListStyled.CustomRow>
          );
        },
      },
      {
        name: 'remote_attendess',
        displayName: i18n.t('eventsList.online'),
        component: (item: any) => {
          return (
            <>
              <ListStyled.CustomRow>
                {item.remote_attendees ? item.remote_attendees : 0}{' '}
                {i18n.t('eventsList.attendees')}
              </ListStyled.CustomRow>
              {item.zoomevent && (
                <ListStyled.CustomZoomDetail
                  target="_blank"
                  to={item.zoomevent.link}
                >
                  <ListStyled.CustomZoomDetailTitle>
                    {i18n.t('reservations.logEventZoom')}
                  </ListStyled.CustomZoomDetailTitle>
                </ListStyled.CustomZoomDetail>
              )}
            </>
          );
        },
      },
      {
        name: 'services',
        displayName: i18n.t('eventsList.extraServices'),
        component: (item: any) => {
          const allServices = getRoom(rooms, item.product).services;
          const selectedServices = allServices.filter((service: any) =>
            item.services.some(
              (selectedService: string) => service.id === selectedService,
            ),
          );
          return (
            <ListStyled.CustomRow>
              {selectedServices.length > 0
                ? selectedServices
                    .map((service: any) => service.name)
                    .join(' | ')
                : '-'}
            </ListStyled.CustomRow>
          );
        },
      },
      {
        name: 'payment',
        displayName: i18n.t('eventsList.payment'),
        component: (item: any) => {
          return (
            <EditText
              variant={EdiTextVariant.Filled}
              defaultValue={item.payment_status}
              select={{
                selections: EventStatusPayment,
                uncontrolledSelect: true,
              }}
              onChange={() => {
                item.payment_status !== 'PAID' && paymentStatusAlertModal(item);
              }}
              minHeight={31}
              disabled={
                item.payment_status === 'PAID' ||
                reservation.status === 'SENT' ||
                reservation.status === 'CANCELED_BY_ADMIN' ||
                reservation.status === 'CANCELED_BY_USER' ||
                props.loading
              }
            />
          );
        },
      },
      {
        name: 'checkIn',
        displayName: '',
        component: (item: any) => {
          if (
            !item.check_in &&
            reservation.status === 'CONFIRMED' &&
            reservation.status !== 'CANCELED_BY_ADMIN' &&
            reservation.status !== 'CANCELED_BY_USER'
          ) {
            return (
              <ClickableTextProps
                clickableText={i18n.t('eventsList.checkIn')}
                iconLeft={SvgType.CheckIn}
                styled={{
                  fontFamily: 'EncodeSans-Regular',
                  fontSize: '10px',
                  color: Colors.Black,
                }}
                onClick={() =>
                  openModalCheckInOut(
                    item,
                    ReservationBookingActionType.CheckIn,
                  )
                }
              />
            );
          } else if (item.check_in) {
            const { time } = getDateTimeBooking(item.check_in);
            const date = getStringDateName(item.check_in);
            return (
              <ListStyled.ContainerCheckInOut>
                <ListStyled.CustomRow>Check-in:</ListStyled.CustomRow>
                <ListStyled.CustomRow>
                  {date} {time}
                </ListStyled.CustomRow>
              </ListStyled.ContainerCheckInOut>
            );
          }
        },
      },
      {
        name: 'checkOut',
        displayName: '',
        component: (item: any) => {
          if (
            item.check_in &&
            !item.check_out &&
            reservation.status !== 'CANCELED_BY_ADMIN' &&
            reservation.status !== 'CANCELED_BY_USER'
          ) {
            return (
              <ClickableTextProps
                clickableText={i18n.t('eventsList.checkOut')}
                iconLeft={SvgType.CheckOut}
                styled={{
                  fontFamily: 'EncodeSans-Regular',
                  fontSize: '10px',
                  color: Colors.Black,
                }}
                onClick={() =>
                  openModalCheckInOut(
                    item,
                    ReservationBookingActionType.CheckOut,
                  )
                }
              />
            );
          } else if (item.check_out) {
            const { time } = getDateTimeBooking(item.check_out);
            const date = getStringDateName(item.check_out);
            return (
              <ListStyled.ContainerCheckInOut>
                <ListStyled.CustomRow>Check-out:</ListStyled.CustomRow>
                <ListStyled.CustomRow>
                  {date} {time}
                </ListStyled.CustomRow>
              </ListStyled.ContainerCheckInOut>
            );
          }
        },
      },
    ],
    [reservation.interval, rooms, props.loading],
  );

  return (
    <EventsListReservationStyled.Container>
      <LinearLoading loading={props.loading} />
      <EventsListReservationStyled.ContainerHeader>
        <EventsListReservationStyled.ContainerHeaderTitle>
          {reservation.interval.length}{' '}
          {i18n.t('handleReservation.eventListHeaderTitle')}
        </EventsListReservationStyled.ContainerHeaderTitle>
        {reservation.status !== 'CANCELED_BY_ADMIN' &&
          reservation.status !== 'CANCELED_BY_USER' && (
            <ClickableTextProps
              clickableText={i18n.t('handleReservation.editReservation')}
              styled={{
                color: '#134d84',
                fontFamily: 'EncodeSans-SemiBold',
                fontSize: '14px',
              }}
              onClick={props.onEditReservationClick}
            />
          )}
      </EventsListReservationStyled.ContainerHeader>

      <EventsListReservationStyled.ContainerContent>
        {reservation.interval && (
          <TableEventsReservation
            events={reservation.interval}
            fields={fields}
            backgroundColor={
              reservation.status === 'CANCELED_BY_ADMIN' ||
              reservation.status === 'CANCELED_BY_USER'
                ? Colors.IceGrey
                : Colors.White
            }
          />
        )}
      </EventsListReservationStyled.ContainerContent>
    </EventsListReservationStyled.Container>
  );
};

export default EventsListReservation;
